import { defineEndpoint, PageQuery, SearchQuery, SearchResult } from '@itc/shared';
import { z } from 'zod';
import { BoardPeriod, Boards } from './boards.ts';
import { TargetDonations } from './targets.ts';
import { ImportMaterials, Material, MaterialInput, MaterialUpdate } from './materials.ts';
import { Delivery, DeliveryInput, DeliveryStatus, DeliveryUpdate, ReceiveInput } from './incoming.ts';
import { PostInput, RequestInput, RequestStatus, RequestUpdate, RequestView } from './requests.ts';
import { AcceptInput, Donation, DonationActions, DonationStatus, People, PickupUpdate, PledgeInput, Preferences, ReasonInput, RecordId, RecordInput, UpdateInput, VersionInput } from './schemas.ts';

// Donations' contracts (design §7.3, plan 2C). Every change takes an
// Idempotency-Key, so a retried request never pledges or accepts twice.
//
// Reads and the donor's own changes are open to any logged-in user, and the
// services decide: a donor may be anyone, and receivers hold view_all, not
// view_own. Server tests refuse each to a stranger.

const ById = z.object({ id: RecordId });

/** A donation with what its viewer may do now, and the people it names. */
export const DonationView = z.object({ donation: Donation, actions: DonationActions, people: People });
export type DonationView = z.infer<typeof DonationView>;

/** Donations, newest first: every one for view_all, otherwise only the viewer's own (as a donor or recorder). */
export const listDonations = defineEndpoint({
  method: 'GET',
  path: '/donations/donations',
  permission: 'authenticated',
  query: PageQuery.extend({ status: DonationStatus.optional() }),
  response: z.object({
    items: z.array(Donation),
    nextCursor: z.string().nullable(),
    people: People,
    /** Open donations a member donor of which has lost access ("Donor left", Assumption 17). */
    donorLeft: z.array(z.string()),
  }),
});

export const getDonation = defineEndpoint({ method: 'GET', path: '/donations/donations/:id', permission: 'authenticated', params: ById, response: DonationView });

/** A pledge, or a donation recorded as yourself. */
export const pledgeDonation = defineEndpoint({ method: 'POST', path: '/donations/donations', permission: 'donations.submit', body: PledgeInput, response: DonationView, idempotencyKey: 'required' });

/** A receiver records a donation for others, and may accept it at once. */
export const recordDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/record', permission: 'donations.receive', body: RecordInput, response: DonationView, idempotencyKey: 'required' });

/** Its donors, its recorder or a receiver change an open donation. */
export const updateDonation = defineEndpoint({ method: 'PUT', path: '/donations/donations/:id', permission: 'authenticated', params: ById, body: UpdateInput, response: DonationView, idempotencyKey: 'required' });

export const handOverDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/hand-over', permission: 'authenticated', params: ById, body: VersionInput, response: DonationView, idempotencyKey: 'required' });

export const withdrawDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/withdraw', permission: 'authenticated', params: ById, body: VersionInput, response: DonationView, idempotencyKey: 'required' });

/** A receiver, or on a target its creator or a manager (T1). The service decides. */
export const acceptDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/accept', permission: 'authenticated', params: ById, body: AcceptInput, response: DonationView, idempotencyKey: 'required' });

export const rejectDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/reject', permission: 'authenticated', params: ById, body: ReasonInput, response: DonationView, idempotencyKey: 'required' });

/** A member donor confirms a donation someone recorded for them: they're credited. */
export const confirmDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/confirm', permission: 'authenticated', params: ById, body: VersionInput, response: DonationView, idempotencyKey: 'required' });

/** A member donor says a donation recorded for them isn't theirs: it's flagged for receivers. */
export const disputeDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/dispute', permission: 'authenticated', params: ById, body: ReasonInput, response: DonationView, idempotencyKey: 'required' });

/** A second receiver approves guests' credit. */
export const approveCredit = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/approve-credit', permission: 'donations.receive', params: ById, body: VersionInput, response: DonationView, idempotencyKey: 'required' });

export const reverseDonation = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/reverse', permission: 'authenticated', params: ById, body: ReasonInput, response: DonationView, idempotencyKey: 'required' });

/** Retries a failed deposit or stock, with the same key. */
export const retrySettlement = defineEndpoint({ method: 'POST', path: '/donations/donations/:id/retry', permission: 'donations.receive', params: ById, body: VersionInput, response: DonationView, idempotencyKey: 'required' });

export const getPreferences = defineEndpoint({ method: 'GET', path: '/donations/preferences', permission: 'authenticated', response: Preferences });

/** The caller's own preferences: opting out of the public boards. */
export const setPreferences = defineEndpoint({ method: 'PUT', path: '/donations/preferences', permission: 'authenticated', body: Preferences, response: Preferences, idempotencyKey: 'required' });

/** A coordinator sets an open donation's pickup logistics. */
export const setDonationPickup = defineEndpoint({ method: 'PUT', path: '/donations/donations/:id/pickup', permission: 'donations.manage_requests', params: ById, body: PickupUpdate, response: DonationView, idempotencyKey: 'required' });

// Requests (plan 2C Assumption 5). Members see Active requests' progress totals and
// their own drafts; coordinators and view_all see every request.

/** What the viewer may do to a request now. */
export const RequestActions = z.object({ edit: z.boolean(), withdraw: z.boolean(), activate: z.boolean(), deny: z.boolean(), fulfil: z.boolean(), reopen: z.boolean(), post: z.boolean(), donate: z.boolean() });
export type RequestActions = z.infer<typeof RequestActions>;

export const RequestDetail = RequestView.extend({ actions: RequestActions, people: People });
export type RequestDetail = z.infer<typeof RequestDetail>;

export const listRequests = defineEndpoint({
  method: 'GET',
  path: '/donations/requests',
  permission: 'authenticated',
  query: z.object({ status: RequestStatus.optional() }),
  response: z.object({ items: z.array(RequestView), people: People, requesterLeft: z.array(z.string()) }),
});

export const getRequest = defineEndpoint({ method: 'GET', path: '/donations/requests/:id', permission: 'authenticated', params: ById, response: RequestDetail });

/** A member submits a draft (donations.submit); a coordinator may create it Active. */
/** A request's card: who to thank. Only for those who may see who gave (`requests.ts`): members see totals, never names. */
export const getRequestThanks = defineEndpoint({ method: 'GET', path: '/donations/requests/:id/thanks', permission: 'authenticated', params: ById, response: z.object({ names: z.array(z.string()) }) });

export const createRequest = defineEndpoint({ method: 'POST', path: '/donations/requests', permission: 'authenticated', body: RequestInput, response: RequestDetail, idempotencyKey: 'required' });

/** Its requester edits their own draft; a coordinator edits any open request. */
export const updateRequest = defineEndpoint({ method: 'PUT', path: '/donations/requests/:id', permission: 'authenticated', params: ById, body: RequestUpdate, response: RequestDetail, idempotencyKey: 'required' });

/** Its requester or a coordinator withdraws a draft: it's deleted. */
export const withdrawRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/withdraw', permission: 'authenticated', params: ById, body: VersionInput, response: z.object({ id: z.string() }), idempotencyKey: 'required' });

export const activateRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/activate', permission: 'donations.manage_requests', params: ById, body: VersionInput, response: RequestDetail, idempotencyKey: 'required' });
export const denyRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/deny', permission: 'donations.manage_requests', params: ById, body: ReasonInput, response: RequestDetail, idempotencyKey: 'required' });
export const fulfilRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/fulfil', permission: 'donations.manage_requests', params: ById, body: VersionInput, response: RequestDetail, idempotencyKey: 'required' });
export const reopenRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/reopen', permission: 'donations.manage_requests', params: ById, body: VersionInput, response: RequestDetail, idempotencyKey: 'required' });

/** "Copy for Discord" in the post sheet: stamps posted_at, and activates a draft with "Mark Active". */
export const postRequest = defineEndpoint({ method: 'POST', path: '/donations/requests/:id/post', permission: 'donations.manage_requests', params: ById, body: PostInput, response: RequestDetail, idempotencyKey: 'required' });

// Expected deliveries and Incoming (Assumption 6).

export const DeliveryView = z.object({ delivery: Delivery, people: People });
export type DeliveryView = z.infer<typeof DeliveryView>;

export const listDeliveries = defineEndpoint({ method: 'GET', path: '/donations/deliveries', permission: 'donations.view_all', query: z.object({ status: DeliveryStatus.optional() }), response: z.object({ items: z.array(Delivery), people: People }) });
export const createDelivery = defineEndpoint({ method: 'POST', path: '/donations/deliveries', permission: 'donations.manage_requests', body: DeliveryInput, response: DeliveryView, idempotencyKey: 'required' });
export const updateDelivery = defineEndpoint({ method: 'PUT', path: '/donations/deliveries/:id', permission: 'donations.manage_requests', params: ById, body: DeliveryUpdate, response: DeliveryView, idempotencyKey: 'required' });
export const cancelDelivery = defineEndpoint({ method: 'POST', path: '/donations/deliveries/:id/cancel', permission: 'donations.manage_requests', params: ById, body: VersionInput, response: DeliveryView, idempotencyKey: 'required' });

/** A receiver receives a delivery: it's stocked by the stock handler. */
export const receiveDelivery = defineEndpoint({ method: 'POST', path: '/donations/deliveries/:id/receive', permission: 'donations.receive', params: ById, body: ReceiveInput, response: DeliveryView, idempotencyKey: 'required' });
export const retryDelivery = defineEndpoint({ method: 'POST', path: '/donations/deliveries/:id/retry', permission: 'donations.receive', params: ById, body: VersionInput, response: DeliveryView, idempotencyKey: 'required' });

/** The Incoming tab: open pledges with pickup logistics and open deliveries, in R12's order. */
export const getIncoming = defineEndpoint({
  method: 'GET',
  path: '/donations/incoming',
  permission: 'donations.view_all',
  response: z.object({ donations: z.array(Donation), deliveries: z.array(Delivery), order: z.array(z.object({ kind: z.enum(['donation', 'delivery']), id: z.string() })), people: People }),
});

// Targets (plan 4B).

/** T3: a target's donations, as this viewer may see them. */
export const getTargetDonations = defineEndpoint({ method: 'GET', path: '/donations/targets/:id', permission: 'donations.view_requests', params: ById, response: TargetDonations });

// The materials list (plan 4B): what materials are worth on the board.

export const listMaterials = defineEndpoint({ method: 'GET', path: '/donations/materials', permission: 'donations.view_requests', response: z.object({ items: z.array(Material) }) });
export const createMaterial = defineEndpoint({ method: 'POST', path: '/donations/materials', permission: 'donations.manage_materials', body: MaterialInput, response: Material, idempotencyKey: 'required' });
export const updateMaterial = defineEndpoint({ method: 'PUT', path: '/donations/materials/:id', permission: 'donations.manage_materials', params: ById, body: MaterialUpdate, response: Material, idempotencyKey: 'required' });
/** FleetCom's own JSON export of its master list. */
export const importMaterials = defineEndpoint({ method: 'POST', path: '/donations/materials/import', permission: 'donations.manage_materials', body: ImportMaterials, response: z.object({ created: z.number().int(), updated: z.number().int() }), idempotencyKey: 'required' });

// The Donations page (plan 2C Chunk 5).

/** The four stats (line 2326): the last 30 days' donations and donors, the top donor, and all time. */
export const DonationStats = z.object({ last30: z.number().int(), donors30: z.number().int(), top: z.object({ name: z.string(), count: z.number().int() }).nullable(), allTime: z.number().int() });
export type DonationStats = z.infer<typeof DonationStats>;

/** The boards for a period, and the stats. Members see the public boards (opted-out donors left out); view_all sees them all, marked. */
/** Who gave to the bank (plan 2E R10), leaving out those who opted out, for every viewer: the bank can't see that preference. */
export const getBankDonors = defineEndpoint({ method: 'GET', path: '/donations/bank-donors', permission: 'donations.view_all', query: z.object({ period: BoardPeriod.default('30') }), response: z.object({ donors: z.array(z.object({ name: z.string(), total: z.number().int(), count: z.number().int() })) }) });

/** R15: what the caller may find, by the same rules as the pages. */
export const searchDonations = defineEndpoint({ method: 'GET', path: '/donations/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

export const getBoards = defineEndpoint({
  method: 'GET',
  path: '/donations/boards',
  permission: 'authenticated',
  query: z.object({ period: BoardPeriod.optional() }),
  response: Boards.extend({ stats: DonationStats }),
});

/** What the donation sheet offers: accounts, places, Active requests and the defaults, read through the bank's and supply's calls. */
export const getSheet = defineEndpoint({
  method: 'GET',
  path: '/donations/sheet',
  permission: 'authenticated',
  response: z.object({
    accounts: z.array(z.object({ id: z.string(), name: z.string(), kind: z.enum(['primary', 'held']), holders: z.array(z.string()) })),
    locations: z.array(z.string()),
    ships: z.array(z.object({ id: z.string(), name: z.string(), hull: z.string(), cargoCapacity: z.number().int().nullable(), used: z.number().int() })),
    requests: z.array(RequestView),
    defaultAccount: z.string().nullable(),
    defaultLocation: z.string(),
  }),
});

/** Supply's items by part of a name, for the sheet's suggestions. */
export const searchItems = defineEndpoint({
  method: 'GET',
  path: '/donations/items',
  permission: 'authenticated',
  query: z.object({ q: z.string().trim().max(80).default('') }),
  response: z.object({ items: z.array(z.object({ name: z.string(), category: z.string(), unit: z.string() })) }),
});

/** Active members by name, for a receiver naming donors or a coordinator naming a collector. */
export const searchPeople = defineEndpoint({
  method: 'GET',
  path: '/donations/people',
  permission: 'authenticated',
  query: z.object({ q: z.string().trim().min(1).max(64) }),
  response: z.object({ people: z.array(z.object({ id: z.string(), displayName: z.string() })) }),
});

/** "Waiting on you" (R15): donations you can accept, credit you can confirm or approve, and failed settlements you accepted. */
export const getWaiting = defineEndpoint({ method: 'GET', path: '/donations/waiting', permission: 'authenticated', response: z.object({ items: z.array(Donation), people: People }) });

// The Requests and Incoming pages (plan 2C Chunk 6). The posts are built in the
// browser from these reads (discord-text.ts), so the preview is the post.

const Places = z.object({ locations: z.array(z.string()), ships: z.array(z.object({ id: z.string(), name: z.string(), hull: z.string(), cargoCapacity: z.number().int().nullable(), used: z.number().int() })) });

/** Supply's places, for the request and delivery sheets, ship names and Incoming's capacity note. */
export const getPlaces = defineEndpoint({ method: 'GET', path: '/donations/places', permission: 'donations.view_requests', response: Places });

/** What `genAcquire` needs: Active requests with progress, and supply's low stock. */
export const getAcquisition = defineEndpoint({
  method: 'GET',
  path: '/donations/acquisition',
  permission: 'donations.view_requests',
  response: z.object({
    requests: z.array(RequestView),
    low: z.array(z.object({ item: z.object({ name: z.string(), category: z.string(), unit: z.enum(['crates', 'scu', 'kits', 'units']) }), place: z.union([z.object({ kind: z.literal('stockpile'), location: z.string() }), z.object({ kind: z.literal('ship'), shipId: z.string() })]), have: z.number().int(), target: z.number().int() })),
  }),
});

/** "What Sierra has on hand right now" for a request's items (`reqStockLines`), from supply's `findItems`. */
export const getRequestStock = defineEndpoint({ method: 'GET', path: '/donations/requests/:id/stock', permission: 'donations.view_requests', params: ById, response: z.object({ stock: z.array(z.string()) }) });

/** Supply's report, for a post's "full supply report". */
export const getSupplyReport = defineEndpoint({ method: 'GET', path: '/donations/supply-report', permission: 'donations.view_requests', response: z.object({ text: z.string() }) });

/** The nav badges (R15): drafts to review, for coordinators; what waits on the caller, for anyone. Worked out by the same functions as the pages. */
export const getCounts = defineEndpoint({ method: 'GET', path: '/donations/counts', permission: 'authenticated', response: z.object({ drafts: z.number().int(), waiting: z.number().int() }) });
